import { useMemo, useState, type JSX } from 'react';
import { AlertTriangle, Loader2, SearchX, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { Pagination } from '@/components/ui/pagination';
import { RangeSelectHint, RowCheckbox, SelectAllCheckbox, selectableRowClass } from '@/components/ui/range-select';
import { useRangeSelection } from '@/hooks/useRangeSelection';
import { cn } from '@/lib/utils';
import { STATUS_FILTER_LABELS, checkHint } from '@/lib/bulk-import/copy';
import type { DistinctNiche, NicheOption } from '@/lib/bulk-import/niches';
import { nicheKey } from '@/lib/bulk-import/niches';
import {
  ROW_STATUS_ORDER,
  countStatuses,
  isSelectable,
  type ImportRow,
  type RowCheck,
  type RowStatus,
} from '@/lib/bulk-import/rows';
import { HandleCell, ProgressBar, RowStatusPill, StepPanel, plural } from './shared';

const PAGE_SIZE = 50;

interface ReviewStepProps {
  rows: ImportRow[];
  statuses: RowStatus[];
  checks: Map<number, RowCheck>;
  checking: { done: number; total: number } | null;
  checkError: string | null;
  onRetryCheck: () => void;
  selected: Set<number>;
  onSelect: (keys: number[], on: boolean) => void;
  /** Distinct niche values in the file. Empty when no niche column is mapped. */
  distinct: DistinctNiche[];
  nicheOptions: NicheOption[];
  nichesLoading: boolean;
  nicheMap: Record<string, string | null>;
  onNicheMap: (key: string, code: string | null) => void;
  onBack: () => void;
  onNext: () => void;
}

type Filter = 'all' | RowStatus;

export function ReviewStep(props: ReviewStepProps): JSX.Element {
  const { rows, statuses, checks, selected } = props;
  const [filter, setFilter] = useState<Filter>('all');
  const [page, setPage] = useState(1);
  const counts = useMemo(() => countStatuses(statuses), [statuses]);
  const nicheName = useMemo(() => new Map(props.nicheOptions.map((n) => [n.code, n.displayName])), [props.nicheOptions]);

  const shown = useMemo(
    () => rows.filter((_, i) => filter === 'all' || statuses[i] === filter),
    [rows, statuses, filter],
  );
  const pageRows = shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const selectableShown = shown.filter((r) => isSelectable(statuses[r.key]!)).map((r) => r.key);
  const pageSelectable = pageRows.filter((r) => isSelectable(statuses[r.key]!)).map((r) => r.key);
  const allShownSelected = selectableShown.length > 0 && selectableShown.every((k) => selected.has(k));
  const rowNumberOf = (key: number | null): number | undefined => (key === null ? undefined : rows[key]?.rowNumber);
  const range = useRangeSelection({
    visible: pageRows.map((r) => r.key),
    isSelected: (key) => selected.has(key),
    onApply: props.onSelect,
    isDisabled: (key) => !isSelectable(statuses[key]!),
  });

  if (props.checking || props.checkError) {
    return (
      <StepPanel title="Checking your creators" description="Seeing who is new, who you've already messaged, and who is already logged.">
        {props.checking && (
          <ProgressBar done={props.checking.done} total={props.checking.total} label="Checked" />
        )}
        {props.checkError && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/[0.06] px-3 py-2">
            <p role="alert" className="text-sm text-destructive-strong">
              Couldn&apos;t check the list: {props.checkError}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={props.onBack}>
                Back
              </Button>
              <Button size="sm" onClick={props.onRetryCheck}>
                Try again
              </Button>
            </div>
          </div>
        )}
      </StepPanel>
    );
  }

  return (
    <div className="space-y-6">
      {props.distinct.length > 0 && (
        <StepPanel
          title="Niches"
          description="Each niche written in your file, matched to one of your team's niches. Change any guess, or pick None to leave it blank."
        >
          {props.nichesLoading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading your team&apos;s niches…
            </p>
          ) : (
            <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
              {props.distinct.map((d) => {
                const code = props.nicheMap[d.key] ?? null;
                return (
                  <div key={d.key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-center gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium" title={d.label}>
                        {d.label}
                      </p>
                      <p className="text-xs text-muted-foreground">{plural(d.count, 'row')}</p>
                    </div>
                    <Combobox
                      aria-label={`Niche for “${d.label}”`}
                      value={code ?? ''}
                      onChange={(v) => props.onNicheMap(d.key, v === '' ? null : v)}
                      options={props.nicheOptions.map((n) => ({ value: n.code, label: n.displayName }))}
                      allowAll
                      allLabel="None"
                      placeholder="None"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </StepPanel>
      )}

      <StepPanel
        title="Review"
        description="Ticked rows go on to the next step. New creators and ones you've messaged are ticked for you."
      >
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by status">
          <FilterChip active={filter === 'all'} onClick={() => { setFilter('all'); setPage(1); }} label="All" count={rows.length} />
          {ROW_STATUS_ORDER.filter((s) => counts[s] > 0).map((s) => (
            <FilterChip
              key={s}
              active={filter === s}
              onClick={() => { setFilter(s); setPage(1); }}
              label={STATUS_FILTER_LABELS[s]}
              count={counts[s]}
            />
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <p className="flex flex-wrap items-baseline gap-x-3 text-muted-foreground">
            <span>
              <span className="font-semibold text-foreground">{plural(selected.size, 'row')}</span> ticked
            </span>
            <RangeSelectHint show={selected.size > 0} />
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={selectableShown.length === 0}
              onClick={() => props.onSelect(selectableShown, !allShownSelected)}
            >
              {allShownSelected ? 'Untick' : 'Tick'} all {filter === 'all' ? '' : 'shown '}({selectableShown.length.toLocaleString('en-US')})
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pageSelectable.length === 0 || pageSelectable.every((k) => selected.has(k))}
              onClick={() => props.onSelect(pageSelectable, true)}
            >
              Tick this page ({pageSelectable.length})
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={selected.size === 0}
              onClick={() => props.onSelect([...selected], false)}
            >
              Untick all
            </Button>
          </div>
        </div>

        {shown.length === 0 ? (
          <EmptyState icon={<SearchX className="h-6 w-6" />} title="No rows with this status." />
        ) : (
          <>
            <DataTable density="outreacher" aria-label="Rows to import">
              <DataTableHeader>
                <tr>
                  <DataTableHeaderCell>
                    <span className="inline-flex items-center gap-3">
                      <SelectAllCheckbox
                        label="Tick every row on this page"
                        state={range.headState}
                        disabled={range.selectable.length === 0}
                        onToggle={range.toggleAll}
                      />
                      Creator
                    </span>
                  </DataTableHeaderCell>
                  <DataTableHeaderCell>Niche</DataTableHeaderCell>
                  <DataTableHeaderCell>Email</DataTableHeaderCell>
                  <DataTableHeaderCell>Status</DataTableHeaderCell>
                  <DataTableHeaderCell numeric>Row</DataTableHeaderCell>
                </tr>
              </DataTableHeader>
              <tbody>
                {pageRows.map((r) => {
                  const status = statuses[r.key]!;
                  const check = checks.get(r.key);
                  const can = isSelectable(status);
                  const on = selected.has(r.key);
                  const mapped = r.niche ? props.nicheMap[nicheKey(r.niche)] ?? null : null;
                  const hint = checkHint(status, check);
                  return (
                    <DataTableRow
                      key={r.key}
                      state={on ? 'selected' : status === 'invalid' || status === 'duplicate' ? 'terminal' : 'default'}
                      className={selectableRowClass(!can)}
                      title={can ? undefined : 'Invalid, duplicate and unchecked rows can’t be imported'}
                      {...range.rowProps(r.key)}
                    >
                      <DataTableCell anchor>
                        <span className="flex min-w-0 items-center gap-3">
                          <RowCheckbox aria-label={`Tick row ${r.rowNumber}`} {...range.checkboxProps(r.key)} />
                          <HandleCell row={r} />
                        </span>
                      </DataTableCell>
                      <DataTableCell data-label="Niche">
                        {r.niche ? (
                          <span className="flex flex-col">
                            <span className={cn(mapped ? 'text-foreground' : 'text-muted-foreground')}>
                              {mapped ? nicheName.get(mapped) ?? mapped : 'None'}
                            </span>
                            <span className="text-xs text-muted-foreground">{r.niche}</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </DataTableCell>
                      <DataTableCell data-label="Email" muted>
                        <span className="block max-w-[26ch] truncate">{r.email ?? '—'}</span>
                      </DataTableCell>
                      <DataTableCell data-label="Status">
                        <span className="flex flex-col items-start gap-1">
                          <RowStatusPill status={status} check={check} row={r} duplicateRow={rowNumberOf(r.duplicateOf)} />
                          {hint && (
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
                              {hint}
                            </span>
                          )}
                        </span>
                      </DataTableCell>
                      <DataTableCell numeric muted data-label="Row">
                        {r.rowNumber}
                      </DataTableCell>
                    </DataTableRow>
                  );
                })}
              </tbody>
            </DataTable>
            <Pagination page={page} limit={PAGE_SIZE} total={shown.length} onPageChange={setPage} />
          </>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
          <p className="flex items-center gap-2 text-xs text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-brand" aria-hidden />
            Nothing is sent until you confirm on the next step.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={props.onBack}>
              Back
            </Button>
            <Button onClick={props.onNext} disabled={selected.size === 0}>
              Next: {plural(selected.size, 'creator')}
            </Button>
          </div>
        </div>
      </StepPanel>
    </div>
  );
}

function FilterChip({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count: number }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors pointer-coarse:h-10',
        active ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-[var(--input-bg)] text-foreground hover:bg-accent',
      )}
    >
      {label}
      <span className={cn('font-mono tabular-nums', active ? 'opacity-80' : 'text-muted-foreground')}>{count.toLocaleString('en-US')}</span>
    </button>
  );
}
